import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Checkbox } from "../../components/ui/checkbox";
import { Badge } from "../../components/ui/badge";
import {
  Home,
  User,
  Phone,
  Lock,
  CheckCircle,
  AlertTriangle,
  LogOut,
  Loader2,
  XCircle,
} from "lucide-react";

export const Profile = () => {
  const navigate = useNavigate();
  const { user, updateProfile, changePassword, logout } = useAuth();

  const [profileData, setProfileData] = useState({
    full_name: user?.full_name || "",
    phone: user?.phone || "",
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [profileError, setProfileError] = useState("");
  const [profileSuccess, setProfileSuccess] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [isLoadingPassword, setIsLoadingPassword] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);

  const handleProfileChange = (e) => {
    setProfileData({
      ...profileData,
      [e.target.name]: e.target.value,
    });
    setProfileError("");
    setProfileSuccess("");
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value,
    });
    setPasswordError("");
    setPasswordSuccess("");
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileError("");
    setProfileSuccess("");
    setIsLoadingProfile(true);

    const result = await updateProfile(profileData);

    if (result.success) {
      setProfileSuccess("Profile updated successfully!");
      if (result.note) {
        setProfileSuccess(
          "Profile updated! Please log in again to see changes in your session."
        );
      }
    } else {
      setProfileError(result.error || "Failed to update profile");
    }

    setIsLoadingProfile(false);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");

    // Validation
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError("New passwords do not match");
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setPasswordError("New password must be at least 8 characters long");
      return;
    }

    setIsLoadingPassword(true);

    const result = await changePassword(
      passwordData.currentPassword,
      passwordData.newPassword
    );

    if (result.success) {
      setPasswordSuccess("Password changed successfully!");
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } else {
      setPasswordError(result.error || "Failed to change password");
    }

    setIsLoadingPassword(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div
      className="min-h-screen py-12 px-4 sm:px-6 lg:px-8"
      style={{ backgroundColor: "#faf9f5" }}
    >
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="flex flex-col sm:flex-row items-center sm:items-start space-y-4 sm:space-y-0 sm:space-x-6">
            {/* Avatar */}
            <div className="relative">
              <div
                className="h-20 w-20 rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-lg ring-4 ring-white"
                style={{
                  background:
                    "linear-gradient(135deg, #c96442 0%, #ea580c 100%)",
                }}
              >
                {getInitials(user?.full_name)}
              </div>
              <div className="absolute bottom-0 right-0 h-6 w-6 bg-green-500 rounded-full border-4 border-white"></div>
            </div>

            {/* User Info */}
            <div className="flex-1 text-center sm:text-left">
              <h1 className="text-3xl font-bold text-gray-900">
                {user?.full_name || "User"}
              </h1>
              <p className="text-gray-600 mt-1">{user?.email}</p>
              <div className="flex flex-wrap gap-2 mt-3 justify-center sm:justify-start">
                {/* User Role Badge */}
                {user?.user_role && (
                  <Badge
                    className="px-3 py-1 text-white"
                    style={{
                      background:
                        "linear-gradient(135deg, #c96442 0%, #ea580c 100%)",
                    }}
                  >
                    {user.user_role === "homeowner" && "🏠 Homeowner"}
                    {user.user_role === "investor" && "📈 Investor"}
                    {user.user_role === "both" && "🏠📈 Both"}
                    {user.user_role === "admin" && "⚙️ Admin"}
                  </Badge>
                )}
                {/* Phone Badge */}
                {user?.phone && (
                  <Badge
                    variant="secondary"
                    className="bg-purple-100 text-purple-800 hover:bg-purple-200"
                  >
                    <Phone className="w-3 h-3 mr-1" />
                    {user.phone}
                  </Badge>
                )}
                {/* Google Auth Badge */}
                {user?.auth_provider === "google" && (
                  <Badge
                    variant="secondary"
                    className="bg-blue-100 text-blue-800 hover:bg-blue-200"
                  >
                    🔐 Google Account
                  </Badge>
                )}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex sm:flex-col gap-2">
              <Button
                variant="outline"
                className="px-4 py-2 rounded-xl flex items-center gap-2"
                onClick={() => navigate("/")}
              >
                <Home className="w-4 h-4" />
                Home
              </Button>
            </div>
          </div>
        </div>

        <div
          className={`grid grid-cols-1 ${
            user?.auth_provider === "google"
              ? "lg:grid-cols-1"
              : "lg:grid-cols-2"
          } gap-6`}
        >
          {/* Update Profile Card */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex items-center mb-6">
              <div
                className="h-12 w-12 rounded-xl flex items-center justify-center mr-4"
                style={{
                  background:
                    "linear-gradient(135deg, #c96442 0%, #ea580c 100%)",
                }}
              >
                <User className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  Profile Information
                </h2>
                <p className="text-sm text-gray-600">
                  Update your personal details
                </p>
              </div>
            </div>

            <form onSubmit={handleProfileSubmit} className="space-y-5">
              {profileError && (
                <Alert variant="destructive" className="animate-shake">
                  <XCircle className="h-4 w-4" />
                  <AlertDescription>{profileError}</AlertDescription>
                </Alert>
              )}

              {profileSuccess && (
                <Alert className="border-green-500 bg-green-50 text-green-900">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertDescription>{profileSuccess}</AlertDescription>
                </Alert>
              )}

              <div>
                <Label
                  htmlFor="full_name"
                  className="text-gray-700 font-medium text-sm mb-2"
                >
                  Full Name
                </Label>
                <Input
                  id="full_name"
                  name="full_name"
                  type="text"
                  required
                  value={profileData.full_name}
                  onChange={handleProfileChange}
                  className="mt-2 h-11 rounded-xl border-gray-200"
                  style={{ backgroundColor: "#f5f4ed" }}
                />
              </div>

              <div>
                <Label
                  htmlFor="phone"
                  className="text-gray-700 font-medium text-sm mb-2"
                >
                  Phone Number
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={profileData.phone}
                  onChange={handleProfileChange}
                  className="mt-2 h-11 rounded-xl border-gray-200"
                  style={{ backgroundColor: "#f5f4ed" }}
                  placeholder="+1 (555) 000-0000"
                />
              </div>

              <Button
                type="submit"
                disabled={isLoadingProfile}
                className="w-full h-11 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all"
                style={{
                  background:
                    "linear-gradient(90deg, #c96442 0%, #ea580c 100%)",
                }}
              >
                {isLoadingProfile ? (
                  <div className="flex items-center justify-center">
                    <Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" />
                    Updating...
                  </div>
                ) : (
                  "Update Profile"
                )}
              </Button>
            </form>
          </div>

          {/* Change Password Card - Only show for email/password users */}
          {user?.auth_provider !== "google" && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="flex items-center mb-6">
                <div
                  className="h-12 w-12 rounded-xl flex items-center justify-center mr-4"
                  style={{
                    background:
                      "linear-gradient(135deg, #c96442 0%, #ea580c 100%)",
                  }}
                >
                  <Lock className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    Change Password
                  </h2>
                  <p className="text-sm text-gray-600">
                    Keep your account secure
                  </p>
                </div>
              </div>

              <form onSubmit={handlePasswordSubmit} className="space-y-5">
                {passwordError && (
                  <Alert variant="destructive" className="animate-shake">
                    <XCircle className="h-4 w-4" />
                    <AlertDescription>{passwordError}</AlertDescription>
                  </Alert>
                )}

                {passwordSuccess && (
                  <Alert className="border-green-500 bg-green-50 text-green-900">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <AlertDescription>{passwordSuccess}</AlertDescription>
                  </Alert>
                )}

                <div>
                  <Label
                    htmlFor="currentPassword"
                    className="text-gray-700 font-medium text-sm mb-2"
                  >
                    Current Password
                  </Label>
                  <Input
                    id="currentPassword"
                    name="currentPassword"
                    type={showPasswords ? "text" : "password"}
                    required
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    className="mt-2 h-11 rounded-xl border-gray-200"
                    style={{ backgroundColor: "#f5f4ed" }}
                  />
                </div>

                <div>
                  <Label
                    htmlFor="newPassword"
                    className="text-gray-700 font-medium text-sm mb-2"
                  >
                    New Password
                  </Label>
                  <Input
                    id="newPassword"
                    name="newPassword"
                    type={showPasswords ? "text" : "password"}
                    required
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    className="mt-2 h-11 rounded-xl border-gray-200"
                    style={{ backgroundColor: "#f5f4ed" }}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Must be at least 8 characters
                  </p>
                </div>

                <div>
                  <Label
                    htmlFor="confirmPassword"
                    className="text-gray-700 font-medium text-sm mb-2"
                  >
                    Confirm New Password
                  </Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showPasswords ? "text" : "password"}
                    required
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    className="mt-2 h-11 rounded-xl border-gray-200"
                    style={{ backgroundColor: "#f5f4ed" }}
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="show-passwords"
                    checked={showPasswords}
                    onCheckedChange={setShowPasswords}
                  />
                  <Label
                    htmlFor="show-passwords"
                    className="text-sm font-normal cursor-pointer"
                  >
                    Show passwords
                  </Label>
                </div>

                <Button
                  type="submit"
                  disabled={isLoadingPassword}
                  className="w-full h-11 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all"
                  style={{
                    background:
                      "linear-gradient(90deg, #c96442 0%, #ea580c 100%)",
                  }}
                >
                  {isLoadingPassword ? (
                    <div className="flex items-center justify-center">
                      <Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" />
                      Changing...
                    </div>
                  ) : (
                    "Change Password"
                  )}
                </Button>
              </form>
            </div>
          )}
        </div>

        {/* Danger Zone */}
        <div
          className="bg-white rounded-2xl shadow-lg p-8 border-2"
          style={{ borderColor: "#fecaca" }}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start">
              <div
                className="h-12 w-12 rounded-xl flex items-center justify-center mr-4"
                style={{ backgroundColor: "#fee2e2" }}
              >
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Sign Out</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Sign out from your account on this device
                </p>
              </div>
            </div>
            <Button
              variant="destructive"
              onClick={handleLogout}
              className="rounded-xl bg-red-600 hover:bg-red-700 text-white shadow-md hover:shadow-lg transition-all flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </Button>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes shake {
          0%,
          100% {
            transform: translateX(0);
          }
          10%,
          30%,
          50%,
          70%,
          90% {
            transform: translateX(-2px);
          }
          20%,
          40%,
          60%,
          80% {
            transform: translateX(2px);
          }
        }
        .animate-shake {
          animation: shake 0.5s;
        }
      `}</style>
    </div>
  );
};
